import { DataStandard, SchemaField } from '@/lib/data-standards/schemas';

// Helper to convert string to PascalCase
function toPascalCase(str: string): string {
  return str
    .replace(/[^a-zA-Z0-9]+/g, ' ')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join('');
}

// Helper to download file
export function downloadFile(content: string, filename: string, mimeType: string): void {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// Full JSON Package - complete export with metadata
export function exportFullPackage(standard: DataStandard): string {
  const exportData = {
    meta: {
      generator: 'AutomotiveAPI',
      version: standard.version,
      generatedAt: new Date().toISOString(),
      standardId: standard.id,
    },
    title: standard.title,
    description: standard.description,
    version: standard.version,
    category: standard.category,
    lastUpdated: standard.lastUpdated,
    schema: standard.schema,
    example: standard.example,
    fields: standard.fields,
  };
  return JSON.stringify(exportData, null, 2);
}

// JSON Schema only export
export function exportJsonSchema(schema: object, title: string): string {
  const schemaObj = schema as Record<string, unknown>;
  const exportSchema = {
    $schema: 'https://json-schema.org/draft/2020-12/schema',
    ...schemaObj,
    title: schemaObj.title || title,
  };
  return JSON.stringify(exportSchema, null, 2);
}

// TypeScript definitions for all fields
export function exportTypeScript(
  schema: object,
  fields: SchemaField[],
  standardName: string,
  version: string
): string {
  const schemaObj = schema as Record<string, unknown>;
  const properties = (schemaObj.properties || {}) as Record<string, Record<string, unknown>>;
  const required = (schemaObj.required as string[]) || [];

  const lines: string[] = [
    `// Generated from AutomotiveAPI ${standardName} v${version}`,
    `// Full type definitions for all ${fields.length} fields`,
    `// Generated on: ${new Date().toISOString().split('T')[0]}`,
    '',
  ];

  const nestedInterfaces: string[] = [];
  const interfaceFields: string[] = [];

  fields.forEach(field => {
    const prop = properties[field.name];
    if (!prop) return;

    const isRequired = field.required ?? required.includes(field.name);
    const tsType = schemaPropertyToTsType(prop, field.name, nestedInterfaces);
    const optionalMarker = isRequired ? '' : '?';
    const description = prop.description as string;

    if (description) {
      interfaceFields.push(`  /** ${description} */`);
    }
    interfaceFields.push(`  ${field.name}${optionalMarker}: ${tsType};`);
  });

  // Add nested interfaces first
  if (nestedInterfaces.length > 0) {
    lines.push(...nestedInterfaces);
  }

  // Add main interface
  const interfaceName = toPascalCase(standardName);
  lines.push(`export interface ${interfaceName} {`);
  lines.push(...interfaceFields);
  lines.push('}');

  return lines.join('\n');
}

function schemaPropertyToTsType(
  prop: Record<string, unknown>,
  name: string,
  nestedInterfaces: string[]
): string {
  if (prop.enum) {
    return (prop.enum as string[]).map(v => `'${v}'`).join(' | ');
  }

  const type = prop.type as string;

  switch (type) {
    case 'string':
      return 'string';
    case 'number':
    case 'integer':
      return 'number';
    case 'boolean':
      return 'boolean';
    case 'array': {
      const items = prop.items as Record<string, unknown>;
      if (items?.type === 'object' && items?.properties) {
        const nestedName = toPascalCase(name) + 'Item';
        generateNestedInterface(items, nestedName, nestedInterfaces);
        return `${nestedName}[]`;
      }
      if (items?.enum) {
        const enumType = (items.enum as string[]).map(v => `'${v}'`).join(' | ');
        return `(${enumType})[]`;
      }
      if (items) {
        return `${schemaPropertyToTsType(items, name, nestedInterfaces)}[]`;
      }
      return 'unknown[]';
    }
    case 'object':
      if (prop.properties) {
        const nestedName = toPascalCase(name);
        generateNestedInterface(prop, nestedName, nestedInterfaces);
        return nestedName;
      }
      return 'Record<string, unknown>';
    default:
      return 'unknown';
  }
}

function generateNestedInterface(
  prop: Record<string, unknown>,
  interfaceName: string,
  nestedInterfaces: string[]
): void {
  const properties = prop.properties as Record<string, Record<string, unknown>>;
  const required = (prop.required as string[]) || [];

  if (!properties) return;

  const lines: string[] = [];
  lines.push(`export interface ${interfaceName} {`);

  Object.entries(properties).forEach(([propName, propDef]) => {
    const isRequired = required.includes(propName);
    const optionalMarker = isRequired ? '' : '?';
    const description = propDef.description as string;

    const tsType = schemaPropertyToTsType(propDef, propName, nestedInterfaces);

    if (description) {
      lines.push(`  /** ${description} */`);
    }
    lines.push(`  ${propName}${optionalMarker}: ${tsType};`);
  });

  lines.push('}');
  lines.push('');

  nestedInterfaces.unshift(lines.join('\n'));
}

// CSV Field Reference export
export function exportCsvFieldReference(fields: SchemaField[], schema: object): string {
  const schemaObj = schema as Record<string, unknown>;
  const properties = (schemaObj.properties || {}) as Record<string, Record<string, unknown>>;
  const required = (schemaObj.required as string[]) || [];

  const headers = ['name', 'type', 'required', 'description', 'format', 'constraints', 'example'];
  const rows: string[] = [headers.join(',')];

  fields.forEach(field => {
    const prop = properties[field.name] || {};
    const isRequired = field.required ?? required.includes(field.name);
    
    // Collect constraints
    const constraints: string[] = [];
    if (prop.minLength !== undefined) constraints.push(`minLength:${prop.minLength}`);
    if (prop.maxLength !== undefined) constraints.push(`maxLength:${prop.maxLength}`);
    if (prop.minimum !== undefined) constraints.push(`min:${prop.minimum}`);
    if (prop.maximum !== undefined) constraints.push(`max:${prop.maximum}`);
    if (prop.pattern) constraints.push('pattern');

    const row = [
      escapeCSV(field.name),
      escapeCSV(field.type),
      isRequired ? 'true' : 'false',
      escapeCSV(field.description || ''),
      escapeCSV((prop.format as string) || ''),
      escapeCSV(constraints.join('; ')),
      escapeCSV(field.example !== undefined ? String(typeof field.example === 'object' ? JSON.stringify(field.example) : field.example) : ''),
    ];
    rows.push(row.join(','));
  });

  return rows.join('\n');
}

function escapeCSV(value: string): string {
  if (value.includes(',') || value.includes('"') || value.includes('\n')) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

// OpenAPI Component export (full schema)
export function exportOpenApiComponent(
  schema: object,
  standardName: string,
  version: string,
  description: string
): string {
  const schemaObj = schema as Record<string, unknown>;
  const schemaName = toPascalCase(standardName);

  // Remove $schema if present
  const { $schema, ...schemaWithoutDraft } = schemaObj;

  const openApiDoc = {
    openapi: '3.0.3',
    info: {
      title: `${standardName} Schema Component`,
      version: version,
      description: `AutomotiveAPI ${standardName} standard - ${description}`,
    },
    paths: {},
    components: {
      schemas: {
        [schemaName]: {
          ...schemaWithoutDraft,
          title: standardName,
        },
      },
    },
  };

  return JSON.stringify(openApiDoc, null, 2);
}

// Get file extension for export format
export function getFileExtension(format: string): string {
  switch (format) {
    case 'full-package':
      return '.json';
    case 'json-schema':
      return '.schema.json';
    case 'typescript':
      return '.d.ts';
    case 'csv':
      return '.csv';
    case 'openapi':
      return '.openapi.json';
    case 'postman':
      return '.postman.json';
    default:
      return '.json';
  }
}

// Get MIME type for export format
export function getMimeType(format: string): string {
  switch (format) {
    case 'typescript':
      return 'text/typescript';
    case 'csv':
      return 'text/csv';
    default:
      return 'application/json';
  }
}
